import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { LieferungAddonCard } from '../../components/LieferungAddonCard';
import axios from 'axios';
import { CheckSquare, AlertCircle, Package, Thermometer, ListChecks, Clock, Check, X, ClipboardCheck, BarChart3, AlertTriangle, ClipboardMinus, ChevronRight, Truck, ShoppingCart, Fuel } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useModules } from '../../contexts/ModulesContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isModuleEnabled, isV2Catalog } = useModules();
  // Lieferschein-Promo nur zeigen wenn das Modul noch nicht im Paket enthalten ist
  const showLieferungPromo = !user?.is_demo && !isV2Catalog && !isModuleEnabled('lieferung');
  const [stats, setStats] = useState(null);
  const [taskStats, setTaskStats] = useState(null);
  const [unreadCounts, setUnreadCounts] = useState({ total: 0 });
  const [mhdStats, setMhdStats] = useState({ expired_today: 0, expiring_soon: 0, total: 0 });
  const [locations, setLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(true);
  const [openOrdersCount, setOpenOrdersCount] = useState(0);
  const [openOrdersList, setOpenOrdersList] = useState([]);
  const [newWegfahrerCases, setNewWegfahrerCases] = useState([]);
  
  // Dialog states
  const [todayTasksOpen, setTodayTasksOpen] = useState(false);
  const [completedTasksOpen, setCompletedTasksOpen] = useState(false);
  const [todayTasks, setTodayTasks] = useState([]);
  const [completedTasks, setCompletedTasks] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(false);

  useEffect(() => {
    fetchStats();
    fetchTaskStats();
    fetchUnreadCounts();
    fetchMhdStats();
    fetchLocations();
    fetchOpenOrders();
    fetchNewWegfahrerCases();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchTaskStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/tasks/stats`);
      setTaskStats(response.data);
    } catch (error) {
      console.error('Failed to fetch task stats:', error);
    }
  };

  const fetchUnreadCounts = async () => {
    try {
      const response = await axios.get(`${API}/admin/task-control/unread-counts`);
      setUnreadCounts(response.data);
    } catch (error) {
      console.error('Failed to fetch unread counts:', error);
    }
  };

  const fetchMhdStats = async () => {
    try {
      const response = await axios.get(`${API}/mhd/dashboard-stats`);
      setMhdStats(response.data);
    } catch (error) {
      console.error('Failed to fetch MHD stats:', error);
    }
  };

  const fetchOpenOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders`);
      const open = (response.data || []).filter(o => o.status === 'open');
      setOpenOrdersCount(open.length);
      setOpenOrdersList(open);
    } catch {
      // Kein Fehler – Feature optional
    }
  };

  const fetchNewWegfahrerCases = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/wegfahrer/cases?status=neu`,
        { headers: { Authorization: `Bearer ${token}` } });
      setNewWegfahrerCases(response.data || []);
    } catch {
      // Modul nicht freigeschaltet → still fail
    }
  };

  const fetchLocations = async () => {
    setLoadingLocations(true);
    try {
      // Use /user/locations for both admin and stationsleitung
      const response = await axios.get(`${API}/user/locations`);
      setLocations(response.data || []);
    } catch (error) {
      console.error('Failed to fetch locations:', error);
      setLocations([]);
    } finally {
      setLoadingLocations(false);
    }
  };


  const openTodayTasks = async () => {
    setTodayTasksOpen(true);
    setLoadingTasks(true);
    try {
      const response = await axios.get(`${API}/admin/tasks/today`);
      setTodayTasks(response.data);
    } catch (error) {
      console.error('Failed to fetch today tasks:', error);
    } finally {
      setLoadingTasks(false);
    }
  };

  const openCompletedTasks = async () => {
    setCompletedTasksOpen(true);
    setLoadingTasks(true);
    try {
      const response = await axios.get(`${API}/admin/tasks/completed`);
      setCompletedTasks(response.data);
    } catch (error) {
      console.error('Failed to fetch completed tasks:', error);
    } finally {
      setLoadingTasks(false);
    }
  };

  if (!stats) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  const statCards = [
    {
      title: 'Offene Bestellungen',
      value: openOrdersCount > 0 ? openOrdersCount : '→',
      subtitle: openOrdersCount > 0
        ? `${openOrdersCount} Bestellung${openOrdersCount > 1 ? 'en' : ''} warten auf WWS-Eingabe`
        : 'Bestellungen verwalten',
      icon: ShoppingCart,
      color: openOrdersCount > 0 ? 'text-amber-600' : 'text-blue-600',
      bgColor: openOrdersCount > 0 ? 'bg-amber-50' : 'bg-blue-50',
      clickable: true,
      onClick: () => navigate('/bestellungen/historie'),
      badge: openOrdersCount > 0 ? openOrdersCount : null,
    },
    {
      title: 'Aufgaben heute',
      value: taskStats?.assigned_tasks || 0,
      subtitle: 'zugewiesene Aufgaben',
      icon: ListChecks,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      clickable: true,
      onClick: openTodayTasks
    },
    {
      title: 'Abgeschlossen',
      value: taskStats ? `${taskStats.completed_today} / ${taskStats.assigned_tasks}` : '0 / 0',
      subtitle: taskStats ? `${taskStats.completion_percentage}% erledigt` : '0%',
      icon: CheckSquare,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      clickable: true,
      onClick: openCompletedTasks,
      showProgress: true,
      progress: taskStats?.completion_percentage || 0
    },
    {
      title: 'MHD',
      value: mhdStats.expired_today > 0 ? mhdStats.expired_today : (mhdStats.expiring_soon > 0 ? mhdStats.expiring_soon : mhdStats.total),
      subtitle: mhdStats.expired_today > 0 ? 'heute ablaufend!' : (mhdStats.expiring_soon > 0 ? 'bald ablaufend' : 'Produkte erfasst'),
      icon: mhdStats.expired_today > 0 ? AlertTriangle : Package,
      color: mhdStats.expired_today > 0 ? 'text-red-600' : (mhdStats.expiring_soon > 0 ? 'text-yellow-600' : 'text-orange-600'),
      bgColor: mhdStats.expired_today > 0 ? 'bg-red-50' : (mhdStats.expiring_soon > 0 ? 'bg-yellow-50' : 'bg-orange-50'),
      clickable: true,
      onClick: () => navigate('/admin/mhd-control'),
      badge: mhdStats.expired_today > 0 ? mhdStats.expired_today : null
    },
    {
      title: 'Temperatur-Alarme',
      value: stats.temp_alerts,
      subtitle: 'ungelesen',
      icon: Thermometer,
      color: 'text-red-600',
      bgColor: 'bg-red-50',
      clickable: true,
      onClick: () => navigate('/admin/temperature-alerts')
    },
    {
      title: 'Aufgaben-Kontrolle',
      value: unreadCounts.total > 0 ? unreadCounts.total : '→',
      subtitle: unreadCounts.total > 0 ? 'neue Erledigungen' : 'Mitarbeiter-Aufgaben prüfen',
      icon: ClipboardCheck,
      color: unreadCounts.total > 0 ? 'text-red-600' : 'text-purple-600',
      bgColor: unreadCounts.total > 0 ? 'bg-red-50' : 'bg-purple-50',
      clickable: true,
      onClick: () => navigate('/admin/task-control'),
      badge: unreadCounts.total > 0 ? unreadCounts.total : null
    },
    {
      title: 'Exports',
      value: '→',
      subtitle: 'Export-Verwaltung • Klicken für Details →',
      icon: ClipboardMinus,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      clickable: true,
      onClick: () => navigate('/admin/exports-overview')
    },
    {
      title: 'Statistik',
      value: '—',
      subtitle: 'In Bearbeitung',
      icon: BarChart3,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      clickable: true,
      onClick: () => navigate('/admin/statistik')
    },
    {
      title: 'Abschriften',
      value: '→',
      subtitle: 'Warenverluste dokumentieren',
      icon: ClipboardMinus,
      color: 'text-amber-600',
      bgColor: 'bg-amber-50',
      clickable: true,
      onClick: () => navigate('/admin/abschriften')
    },
    {
      title: 'Lieferung',
      value: '→',
      subtitle: 'Lieferscheine prüfen',
      icon: Truck,
      color: 'text-cyan-600',
      bgColor: 'bg-cyan-50',
      clickable: true,
      onClick: () => navigate('/lieferung')
    }
  ];

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <div className="flex-1">
            <h1 className="text-3xl font-bold" style={{fontFamily: 'Manrope, sans-serif'}} data-testid="admin-dashboard-title">
              Admin Dashboard
            </h1>
            <p className="text-muted-foreground mt-2">Willkommen! Hier ist Ihre Tagesübersicht.</p>
          </div>
          
          {/* Location Status Badge */}
          {!loadingLocations && (
            locations.length > 0 ? (
              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                ✔ {locations.length} Standort{locations.length > 1 ? 'e' : ''} aktiv
              </Badge>
            ) : (
              <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">
                ⚠ Kein Standort
              </Badge>
            )
          )}
        </div>

        {/* No Location Warning - Stationsleitung & Admin */}
        {!loadingLocations && locations.length === 0 && (
          <Card className="border-orange-300 bg-orange-50">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-orange-100 rounded-lg">
                  <AlertCircle className="h-6 w-6 text-orange-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-orange-900 mb-1">
                    Kein Standort zugewiesen
                  </h3>
                  <p className="text-sm text-orange-700">
                    Ihnen wurde noch kein Standort zugewiesen. Bitte kontaktieren Sie den Administrator, um Zugriff auf die Module zu erhalten.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Lieferschein-KI-Manager Add-on Upsell – nur für Legacy-Kunden die das Modul noch nicht haben */}
        {showLieferungPromo && <LieferungAddonCard showOnlyWhenInactive={true} />}

        {/* WEGFAHRER-ALERT – Rot, prominenter als Bestellungen */}
        {newWegfahrerCases.length > 0 && (
          <Card className="border-red-400 bg-red-50 dark:bg-red-950/30 dark:border-red-700 shadow-md">
            <CardHeader className="pb-3">
              {/* Mobile: gestapelt, Desktop: nebeneinander */}
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-red-100 dark:bg-red-900/50 rounded-lg animate-pulse shrink-0 mt-0.5">
                    <Fuel className="h-5 w-5 text-red-600 dark:text-red-400" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <CardTitle className="text-base text-red-800 dark:text-red-300">
                      {newWegfahrerCases.length} neuer Wegfahrer-Fall{newWegfahrerCases.length > 1 ? '' : ''}
                    </CardTitle>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <span className="text-xs font-semibold bg-red-600 text-white px-2 py-0.5 rounded-full">
                        SOFORT HANDELN
                      </span>
                      <CardDescription className="text-red-700 dark:text-red-400 text-xs">
                        {newWegfahrerCases.length === 1
                          ? `${newWegfahrerCases[0].fuel_type} – ${newWegfahrerCases[0].liters?.toFixed(1)}L – ${newWegfahrerCases[0].amount?.toFixed(2)}€`
                          : `${newWegfahrerCases.length} Fälle warten auf Bearbeitung`}
                      </CardDescription>
                    </div>
                  </div>
                </div>
                <Button size="sm"
                  className="bg-red-600 hover:bg-red-700 text-white gap-1 w-full sm:w-auto"
                  onClick={() => navigate('/admin/wegfahrer')}>
                  <ChevronRight className="h-4 w-4" />
                  Jetzt bearbeiten
                </Button>
              </div>
            </CardHeader>
            {newWegfahrerCases.length > 1 && (
              <CardContent className="pt-0">
                <div className="space-y-1">
                  {newWegfahrerCases.slice(0, 3).map(c => (
                    <button key={c.id}
                      onClick={() => navigate(`/admin/wegfahrer/${c.id}`)}
                      className="w-full flex items-center justify-between p-2 bg-red-100/60 dark:bg-red-900/20 rounded-lg hover:bg-red-200/60 transition-colors text-left">
                      <span className="text-xs font-mono font-semibold text-red-700 dark:text-red-300">{c.case_nr}</span>
                      <span className="text-xs text-red-600 dark:text-red-400">
                        {c.fuel_type} · {c.liters?.toFixed(1)}L · {c.amount?.toFixed(2)}€
                      </span>
                    </button>
                  ))}
                </div>
              </CardContent>
            )}
          </Card>
        )}

        {/* Offene Bestellungen – Prominent anzeigen wenn vorhanden */}
        {openOrdersList.length > 0 && (
          <Card className="border-amber-300 bg-amber-50 dark:bg-amber-900/10 dark:border-amber-700">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                    <ShoppingCart className="h-5 w-5 text-amber-600" />
                  </div>
                  <div>
                    <CardTitle className="text-base text-amber-800 dark:text-amber-300">
                      {openOrdersList.length} offene Bestellung{openOrdersList.length > 1 ? 'en' : ''} – WWS-Eingabe ausstehend
                    </CardTitle>
                    <CardDescription className="text-amber-700 dark:text-amber-400 text-xs">
                      Bitte in WWS/Extern bestellen und Bestellnummer eintragen
                    </CardDescription>
                  </div>
                </div>
                <Button size="sm" variant="outline"
                  className="border-amber-400 text-amber-700 hover:bg-amber-100 shrink-0"
                  onClick={() => navigate('/bestellungen/historie')}>
                  Alle anzeigen →
                </Button>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="space-y-2">
                {openOrdersList.slice(0, 3).map(order => (
                  <div key={order.id}
                    className="flex items-center justify-between p-2.5 bg-white dark:bg-gray-800 rounded-lg border border-amber-200 dark:border-amber-700 cursor-pointer hover:border-amber-400 transition-colors"
                    onClick={() => navigate('/bestellungen/historie')}>
                    <div className="flex items-center gap-2.5 min-w-0">
                      <span className="text-lg shrink-0">{order.category_icon || '📦'}</span>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">{order.category_name}</p>
                        <p className="text-xs text-muted-foreground">
                          {order.items?.length || 0} Artikel • {new Date(order.created_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Badge variant="outline" className="text-xs bg-amber-100 text-amber-700 border-amber-300">
                        🟡 Offen
                      </Badge>
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </div>
                ))}
                {openOrdersList.length > 3 && (
                  <p className="text-xs text-center text-amber-600 pt-1">
                    + {openOrdersList.length - 3} weitere offene Bestellung{openOrdersList.length - 3 > 1 ? 'en' : ''}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stat Cards Grid */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {statCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Card 
                key={index} 
                data-testid={`admin-stat-card-${index}`}
                className={stat.clickable ? 'cursor-pointer hover:shadow-lg transition-shadow hover:border-primary' : ''}
                onClick={stat.clickable && stat.onClick ? stat.onClick : undefined}
              >
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">{stat.title}</CardTitle>
                  <div className={`p-2 rounded-md ${stat.bgColor}`}>
                    <Icon className={`h-4 w-4 ${stat.color}`} />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold" style={{fontFamily: 'Manrope, sans-serif'}}>
                    {stat.value}
                  </div>
                  {stat.showProgress && (
                    <div className="mt-2">
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-green-600 h-2 rounded-full transition-all"
                          style={{ width: `${stat.progress}%` }}
                        />
                      </div>
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">
                    {stat.subtitle}
                    {stat.clickable && ' • Klicken für Details →'}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Freunde werben Freunde */}
        <Card data-testid="referral-program-card">
          <CardHeader>
            <CardTitle>🤝 Freunde werben Freunde</CardTitle>
            <CardDescription>
              Empfehlen Sie AllFlowX weiter und sparen Sie bei Ihrem eigenen Abo
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Teilen Sie Ihren Empfehlungslink und erhalten Sie <strong>40,00&nbsp;€ Rabatt (inkl. USt)</strong> auf Ihre nächste Rechnung – für jeden geworbenen Neukunden, der seine erste reguläre Rechnung tatsächlich bezahlt.
            </p>
            <button
              onClick={() => navigate('/admin/referrals')}
              data-testid="referral-dashboard-link"
              className="w-full flex items-center justify-between p-3 border rounded-lg hover:bg-accent transition-colors"
            >
              <div className="text-left">
                <p className="text-sm font-medium">Zum Empfehlungs-Dashboard</p>
                <p className="text-xs text-muted-foreground">Ihren Link teilen und 40 € Rabatt sichern</p>
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            </button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Willkommen bei AllFlowX</CardTitle>
            <CardDescription>
              Ihre Plattform für Betriebs- und Mitarbeiterorganisation
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Als Administrator können Sie Standorte, Mitarbeiter und Aufgaben verwalten.
              Nutzen Sie das Menü links, um auf alle Module zuzugreifen.
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Today's Tasks Dialog - MOBILE OPTIMIZED */}
      <Dialog open={todayTasksOpen} onOpenChange={setTodayTasksOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto w-[95vw] sm:w-full">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
              <ListChecks className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600 flex-shrink-0" />
              Aufgaben heute
            </DialogTitle>
          </DialogHeader>
          
          {loadingTasks ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : todayTasks.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <ListChecks className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="text-sm sm:text-base">Keine Aufgaben für heute zugewiesen</p>
            </div>
          ) : (
            <div className="space-y-2 sm:space-y-3 py-2 sm:py-4">
              {todayTasks.map((task) => (
                <div 
                  key={task.id}
                  className={`p-3 sm:p-4 rounded-lg border ${
                    task.is_completed ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
                  }`}
                >
                  <div className="flex items-start gap-2">
                    <div className="shrink-0 mt-0.5">
                      {task.is_completed
                        ? <Check className="h-4 w-4 text-green-600" />
                        : <Clock className="h-4 w-4 text-gray-400" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`font-medium text-sm break-words ${task.is_completed ? 'line-through text-gray-500' : ''}`}>
                        {task.name}
                      </p>
                      {task.description && (
                        <p className="text-xs text-muted-foreground mt-0.5 break-words">{task.description}</p>
                      )}
                      {task.is_completed && task.completed_by_name && (
                        <p className="text-xs text-green-700 mt-1">
                          ✓ {task.completed_by_name}
                          {task.completed_at && (
                            <span className="ml-1 opacity-75">· {new Date(task.completed_at).toLocaleTimeString('de-DE', {hour:'2-digit', minute:'2-digit'})} Uhr</span>
                          )}
                        </p>
                      )}
                    </div>
                    <Badge variant={task.is_completed ? "default" : "outline"} className="text-xs shrink-0">
                      {task.is_completed ? 'Erledigt' : 'Offen'}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Completed Tasks Dialog - MOBILE OPTIMIZED */}
      <Dialog open={completedTasksOpen} onOpenChange={setCompletedTasksOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto w-[95vw] sm:w-full">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
              <CheckSquare className="h-4 w-4 sm:h-5 sm:w-5 text-green-600 flex-shrink-0" />
              Abgeschlossene Aufgaben heute
            </DialogTitle>
          </DialogHeader>
          
          {loadingTasks ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : completedTasks.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <CheckSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Noch keine Aufgaben heute erledigt</p>
            </div>
          ) : (
            <div className="space-y-3 py-4">
              {completedTasks.map((completion, index) => (
                <div 
                  key={index}
                  className="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-200"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-100 rounded-full">
                      <Check className="h-4 w-4 text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium">{completion.task_name}</p>
                      <p className="text-sm text-muted-foreground">
                        erledigt von <span className="font-medium">{completion.completed_by_name || completion.user_name || '–'}</span>
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-green-700">
                      {new Date(completion.completed_at).toLocaleTimeString('de-DE', {hour: '2-digit', minute: '2-digit'})} Uhr
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};
